import * as Sentry from "@sentry/nextjs";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getIntl, getTranslatedMessage } from "../../../Lib/Utils/i18n";
import { isNolAdmission } from "../../../Lib/Utils/nolCard";
import { hideLoader, showLoader } from "../../../Lib/Utils/validationUtil";
import { IFulfillment } from "../../../Orchestrator/Types/Index";
import { IShareTicketParams } from "../../../Orchestrator/Types/Interface/ActionParams/IShareTicketActionParams";
import { showNotification } from "../../../Services/notificationService";

// Long enough for Chrome's "Download multiple files" prompt to be answered
// before the blob URLs it is holding back are released.
const REVOKE_BLOB_URL_AFTER_MS = 60_000;
// A stalled PDF request counts as a failed ticket rather than holding the
// loader indefinitely.
const PDF_FETCH_TIMEOUT_MS = 30_000;

type Booking = IShareTicketParams["booking"];
type BookedOffer = NonNullable<NonNullable<Booking["booking"]>["bookedOffers"]>[number];
type Admission = NonNullable<BookedOffer["admissions"]>[number];
type Passenger = NonNullable<NonNullable<Booking["booking"]>["passengers"]>[number];

// Where an admission sits in the booking.
interface IPlacement {
  admission: Admission;
  passenger?: Passenger;
  passengerIndex: number;
  offerIndex: number;
}

interface ITicketToFetch {
  fulfillment: IFulfillment;
  pdfUrl: string;
  placement: IPlacement;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// No double-tap guard here: showLoader() puts up a full-screen overlay that
// normally blocks the page (and the multi-booking drawer) until the send ends.
// The loader is a single on/off switch, so another request finishing mid-send
// can lift it early; overlapping sends still each deliver their own tickets.
export class ShareTicketAction extends BaseAction<void, IShareTicketParams> {

  // ponytail: Safari and Firefox drop back-to-back programmatic downloads, so
  // there they are spaced out, as the multi-download package does. Chromium
  // (which asks once to allow multiple downloads) gets them all at once.
  protected downloadGapMs = 1_000;

  // PDFs already fetched, keyed by ticket (see pdfKey). A second tap then
  // shares without awaiting the network, so it is still inside the user
  // gesture that Safari requires for navigator.share. Only one booking's PDFs
  // are kept; clearCache() drops them when the booking page goes away.
  private readonly pdfCache = new Map<string, Blob>();
  private cachedBookingCode?: string;

  clearCache(): void {
    this.pdfCache.clear();
    this.cachedBookingCode = undefined;
  }

  getPdfUrl = (fulfillment: IFulfillment): string | undefined => {
    return fulfillment.fulfillmentDocuments?.find(
      (doc: any) => doc.medium === "PDF_A4" && doc.type === "TICKET"
    )?.downloadLink;
  }

  private isMobileDevice(): boolean {
    // iPadOS Safari reports a Mac user agent; touch points give it away.
    const isIPad = /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
    return isIPad || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Where each admission sits in the booking: its passenger (and their
  // index) and its offer index. Built once per send.
  private placements(booking: Booking): Map<string, IPlacement> {
    const passengers = booking.booking?.passengers ?? [];
    const result = new Map<string, IPlacement>();
    (booking.booking?.bookedOffers ?? []).forEach((offer, offerIndex) => {
      offer.admissions?.forEach((admission) => {
        // An id seen twice (e.g. left behind by an exchange): keep a FULFILLED entry.
        if (result.has(admission.id) && admission.status !== "FULFILLED") return;
        const passengerIndex = passengers.findIndex((p) => admission.passengerIds?.includes(p.id));
        result.set(admission.id, {
          admission,
          passenger: passengers[passengerIndex],
          passengerIndex: passengerIndex === -1 ? passengers.length : passengerIndex,
          offerIndex,
        });
      });
    });
    return result;
  }

  // The API lists fulfillments last passenger first; put them back in the
  // booking's passenger order, then trip order (bookedOffers) per passenger.
  private inPassengerOrder(tickets: ITicketToFetch[]): ITicketToFetch[] {
    return [...tickets].sort(({ placement: a }, { placement: b }) =>
      a.passengerIndex - b.passengerIndex || a.offerIndex - b.offerIndex
    );
  }

  // "C6PL5M-Manel-BOUTARFA-2.pdf": numbered per name, so a passenger's
  // outbound and return tickets (or two same-named passengers) don't collide.
  private fileNames(booking: Booking, tickets: ITicketToFetch[]): string[] {
    const counts = new Map<string, number>();
    return tickets.map(({ placement: { passenger } }) => {
      const stem = [booking.booking?.bookingCode, passenger?.detail?.firstName, passenger?.detail?.lastName]
        .filter(Boolean)
        .join("-")
        .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "") // bidi controls
        .replace(/[\x00-\x1f\x7f\\/:*?"<>|\s]+/g, "_") || "ticket";
      const n = (counts.get(stem) ?? 0) + 1;
      counts.set(stem, n);
      return `${stem}-${n}.pdf`;
    });
  }

  // A re-issued ticket is a new fulfillment (new creation time / control
  // number) even when it keeps its download link, so it is fetched fresh,
  // while a plain booking refresh keeps the PDFs already fetched.
  private pdfKey({ fulfillment, pdfUrl }: ITicketToFetch): string {
    return `${pdfUrl}|${fulfillment.createdOn ?? ""}|${fulfillment.controlNumber ?? ""}`;
  }

  private async fetchPdf(ticket: ITicketToFetch, bookingCode?: string): Promise<Blob> {
    const key = this.pdfKey(ticket);
    const cached = this.pdfCache.get(key);
    if (cached) return cached;
    const signal = typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(PDF_FETCH_TIMEOUT_MS) : undefined;
    const response = await fetch(ticket.pdfUrl, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    // Another booking's send may have taken the cache over meanwhile.
    if (this.cachedBookingCode === bookingCode) this.pdfCache.set(key, blob);
    return blob;
  }

  private download(file: File): void {
    const a = document.createElement('a');
    a.download = file.name;
    a.style.display = 'none';
    a.href = URL.createObjectURL(file);
    a.addEventListener('click', () => {
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, REVOKE_BLOB_URL_AFTER_MS);
    });
    document.body.appendChild(a);
    a.click();
  }

  private async downloadAll(files: File[]): Promise<void> {
    // navigator.userAgentData exists in Chromium only.
    const gapMs = "userAgentData" in navigator ? 0 : this.downloadGapMs;
    for (const [index, file] of files.entries()) {
      if (index > 0 && gapMs > 0) await wait(gapMs);
      this.download(file);
    }
  }

  private notifyMissing(failed: number, total: number): void {
    if (failed === total) {
      showNotification("error", getTranslatedMessage("ticketsDownloadFailedTitle"), getTranslatedMessage("ticketsDownloadFailed"));
      return;
    }
    showNotification(
      "warning",
      getTranslatedMessage("ticketsPartlyDownloadedTitle"),
      getIntl().formatMessage({ id: "ticketsPartlyDownloaded" }, { failed, total }),
      6
    );
  }

  // Fulfilled train tickets with a PDF, in passenger order. A nol pass is its
  // own admission (and fulfillment, rendered as a separate "Travel Pass" PDF),
  // and is left out.
  private ticketsToSend(booking: Booking, tripId?: string): ITicketToFetch[] {
    let fulfillments = booking.booking?.fulfillments || [];
    if (tripId) {
      // Every offer on the trip: after an exchange, the first match can hold
      // no fulfilled ticket.
      const admissionsIds = (booking.booking?.bookedOffers ?? [])
        .filter(i => i?.tripCoverage?.coveredTripId === tripId)
        .flatMap(i => i.admissions?.map(a => a.id) ?? []);
      fulfillments = fulfillments.filter(i => admissionsIds.includes(i.id));
    }
    const placements = this.placements(booking);
    const tickets = fulfillments.flatMap((fulfillment): ITicketToFetch[] => {
      const placement = placements.get(fulfillment.id);
      const isTrainTicket = placement?.admission.status === "FULFILLED" && !isNolAdmission(placement.admission);
      const pdfUrl = this.getPdfUrl(fulfillment);
      return placement && isTrainTicket && pdfUrl ? [{ fulfillment, pdfUrl, placement }] : [];
    });
    return this.inPassengerOrder(tickets);
  }

  // Every ticket's PDF as a File; one failing ticket must not block the rest.
  private async fetchFiles(booking: Booking, tickets: ITicketToFetch[]): Promise<File[]> {
    const names = this.fileNames(booking, tickets);
    const fetched = await Promise.all(tickets.map(async (ticket, index) => {
      const { fulfillment } = ticket;
      try {
        const blob = await this.fetchPdf(ticket, booking.booking?.bookingCode);
        return new File([blob], names[index], { type: 'application/pdf' });
      } catch (error) {
        console.warn(`[ShareTicket] Failed to fetch ticket ${fulfillment.id}:`, error);
        Sentry.captureException(error, {
          level: "warning",
          tags: { action: "ShareTicket" },
          extra: { fulfillmentId: fulfillment.id },
        });
        return null;
      }
    }));
    return fetched.filter((f): f is File => f !== null);
  }

  // Tries Web Share on mobile; true when nothing is left to download. canShare
  // is synchronous and checks the real files, so a share that can't succeed
  // is skipped.
  private async sharedOrHinted(files: File[], canSecondTapShare: boolean): Promise<boolean> {
    if (!('canShare' in navigator) || !navigator.canShare({ files })) return false;
    try {
      await navigator.share({ files, title: getTranslatedMessage("ticketsShareTitle") });
      return true;
    } catch (error: unknown) {
      const name = error instanceof Error ? error.name : "";
      if (name === "AbortError") return true; // the user cancelled
      // The gesture expired while fetching (typical on iOS). Mobile browsers
      // tend to keep only the first of several downloads, so instead of
      // downloading, ask for a second tap, which shares from the cache with no
      // wait; downloading now as well would leave the user with duplicates.
      // canSecondTapShare is false when the PDFs were already cached (a second
      // tap wouldn't help, and this is how the hint never repeats) or when
      // tickets are missing (that warning already says to try again). Other
      // errors would just repeat on a second tap, so they download.
      if (name === "NotAllowedError" && canSecondTapShare) {
        showNotification("info", getTranslatedMessage("ticketsShareRetryTitle"), getTranslatedMessage("ticketsShareRetry"), 6);
        return true;
      }
      return false;
    }
  }

  override async run(): Promise<void> {
    const params = this.params;
    // The multi-booking drawer can send before the booking has loaded.
    if (!params?.booking?.booking) return;
    try {
      showLoader();
      const { booking, tripId } = params;

      if (booking.booking?.bookingCode !== this.cachedBookingCode) {
        this.pdfCache.clear();
        this.cachedBookingCode = booking.booking?.bookingCode;
      }

      const tickets = this.ticketsToSend(booking, tripId);
      if (tickets.length === 0) {
        showNotification("info", getTranslatedMessage("ticketsNoneToSendTitle"), getTranslatedMessage("ticketsNoneToSend"));
        return;
      }

      const isFromCache = tickets.every((ticket) => this.pdfCache.has(this.pdfKey(ticket)));
      const files = await this.fetchFiles(booking, tickets);
      const hasMissing = files.length < tickets.length;
      if (hasMissing) {
        this.notifyMissing(tickets.length - files.length, tickets.length);
      }
      if (files.length === 0) {
        return;
      }

      const isMobile = this.isMobileDevice();
      const canSecondTapShare = files.length > 1 && !hasMissing && !isFromCache;
      if (isMobile && (await this.sharedOrHinted(files, canSecondTapShare))) {
        return;
      }

      // Several downloads on a phone: mobile browsers may block all but the
      // first, so say what to expect.
      if (isMobile && files.length > 1) {
        showNotification("info", getTranslatedMessage("ticketsDownloadingSeparatelyTitle"), getTranslatedMessage("ticketsDownloadingSeparately"), 6);
      }

      // Desktop or mobile fallback: one download per ticket.
      await this.downloadAll(files);
    } catch (error) {
      console.error("[ShareTicket] Error:", error);
      Sentry.captureException(error, { tags: { action: "ShareTicket" } });
      showNotification("error", getTranslatedMessage("ticketsDownloadFailedTitle"), getTranslatedMessage("ticketsDownloadFailed"));
    } finally {
      hideLoader();
    }
  }
}

export const shareTicketAction = new ShareTicketAction();
